<template>
  <RevViewWrapper
    revType="improvementIdea"
    :organizationId="organizationId"
    :revId="revId"
    :mode="mode"
    :rev="rev"
    :resetRev="resetRev"
    :get="get"
    :update="update"
    :translate="translateRev"
    :like="like"
    :unlike="unlike"
    :approve="approve"
    :disapprove="disapprove"
    :createValidationRequest="createValidationRequest"
    :cancelValidationRequest="cancelRequest"
    :complete="complete"
    :goToTab="goToStep"
    :setCoach="setCoach"
    :canCompleteRev="canCompleteRev"
  >
    <div
      class="ma-0 pa-0"
      slot-scope="{
        isEditMode,
        organizationId,
        accountSiteId,
        accountId,
        edit,
        canComplete,
        delete: canDelete,
        isApproved,
        isTranslating,
        isTranslated,
        validate,
        iconOptions,
        menuOptions,
        revId,
        errors,
        revTags,
        saveRev,
        completeRev,
        likeRev,
        unlikeRev,
        approveRev,
        disapproveRev,
        implementRev,
        highlightRev,
        setValidationRequest,
        setCancelRequest,
        handleCancelClick,
        translateRev,
        setEditMode,
        setViewMode,
        setRevChanged,
        finishRev,
        isSaving,
        hasCompleteError,
        removeApproval,
        approve,
        revApprovals,
        isImplementedValidated,
        hasRequestValidation,
        canDoFinancialValidation,
        implement,
        isLiked,
        showImplementError,
        plannerIntegration,
        replicateRev,
      }"
    >
      <v-container class="ma-0 pa-0">
        <ChangedStatusAlert
          v-if="hasChangedStatusReason"
          class="mt-1"
          data-testid="improvementIdeaView-changedStatusAlert-alert"
          :data="rev"
        />
        <v-row class="my-0 py-0">
          <v-col md="8">
            <v-card elevation="2" rounded>
              <Header
                likeTid="improvementIdea-button-like"
                likedTid="improvementIdea-button-liked"
                shareType="Rev"
                revType="improvementIdea"
                :isEditMode="isEditMode"
                :organizationId="organizationId"
                :accountSiteId="accountSiteId"
                :accountId="accountId"
                :rev="rev"
                :mediaObject="backgroundHeaderImage"
                :availableCoaches="availableCoaches"
                :availableOwners="availableOwners"
                :availableCollaborators="availableCollaborators"
                :isLiked="isLiked"
                :isApproved="isApproved"
                :iconOptions="iconOptions"
                :menuOptions="menuOptions"
                :deleteRev="deleteRev"
                :trackCancelDelete="trackCancelDelete"
                :trackUpdateRev="trackUpdateRev"
                :trackPrintRev="trackPrintRev"
                :trackStartDelete="trackStartDelete"
                :setCoach="setCoach"
                :setOwner="setOwner"
                :setCollaborators="setCollaborators"
                :updateName="updateName"
                :isSaving="isSaving"
                :canSave="canSave"
                :isTranslating="isTranslating"
                :showStatusModal="showStatusModal"
                :revApprovals="revApprovals"
                :isImplementedValidated="isImplementedValidated"
                :hasRequestValidation="hasRequestValidation"
                :plannerIntegration="plannerIntegration"
                @editRev="setEditMode"
                @save="saveRev(revToUpdate)"
                @approve="approveRev"
                @disapprove="disapproveRev"
                @like="likeRev"
                @unlike="unlikeRev"
                @toggleTranslate="translateRev"
                @revChanged="setRevChanged"
                @cancel="handleCancelClick()"
                @validate="handleValidateClick"
                @statusChanged="
                  $event === $constants.revStatus.completed
                    ? completeRev(canCompleteRev)
                    : handleStatusChanged($event)
                "
                @hideModal="showStatusModal = false"
                @changeModalStatus="handleModalStatusChanged($event)"
                @implement="implementRev(revToUpdate)"
                @highlight="highlightRev(revToUpdate)"
                @replicateRev="replicateRev(rev)"
              />
            </v-card>
            <PredictedFinancialImpactAlert
              data-testid="improvementIdeaView-alert-predictedFinancialImpact"
              :rev="rev"
            />
            <Stepper
              class="my-2"
              :steps="STEPS"
              :phases="phases"
              :activeStep="activeStep"
              :hasCompleteError="hasCompleteError"
              :showImplementError="showImplementError"
              :hasRequestValidation="hasRequestValidation"
              @click="goToStep($event, 'stepper')"
            />
            <v-card class="pa-8">
              <Relate
                v-show="activeStep === STEPS.RELATE"
                hasNextButton
                :isTranslating="isTranslating"
                :isSaving="isSaving"
                :organizationId="organizationId"
                :isEditMode="isEditMode"
                :rev="rev"
                :markRequiredFields="hasCompleteError"
                @revChanged="setRevChanged"
                @next="goToStep(2)"
              />

              <Experiment
                v-show="activeStep === STEPS.EXPERIMENT"
                hasNextButton
                hasBackButton
                :isTranslating="isTranslating"
                :isSaving="isSaving"
                :isEditMode="isEditMode"
                :rev="rev"
                :isRelateComplete="isRelateComplete"
                :markRequiredFields="hasCompleteError"
                @revChanged="setRevChanged"
                @next="goToStep(3)"
                @back="goToStep(1)"
              />
              <Verify
                v-show="activeStep === STEPS.VERIFY"
                hasNextButton
                hasBackButton
                :isTranslating="isTranslating"
                :isEditMode="isEditMode"
                :rev="rev"
                :incompletePhases="!isRelateComplete || !isExperimentComplete"
                :markRequiredFields="hasCompleteError"
                :showStatusModal="showStatusModal"
                @finishRev="finishRev(revToUpdate)"
                @revChanged="setRevChanged"
                @next="goToStep(4)"
                @back="goToStep(2)"
                @cancelRev="handleModalStatusChanged($event)"
                @setViewMode="setViewMode"
              />
              <Expand
                v-show="activeStep === STEPS.EXPAND"
                hasNextButton
                hasBackButton
                :isTranslating="isTranslating"
                :isEditMode="isEditMode"
                :isSaving="isSaving"
                :rev="rev"
                :incompletePhases="
                  !isRelateComplete ||
                  !isExperimentComplete ||
                  !isVerifyComplete
                "
                :markRequiredFields="hasCompleteError"
                :canComplete="canComplete && hasSolution"
                @revChanged="setRevChanged"
                @completeRev="completeRev(canCompleteRev)"
                @goToStep="goToStep"
                @setEditMode="setEditMode"
                @setViewMode="setViewMode"
                @next="goToStep(5)"
                @back="goToStep(3)"
              />
              <Review
                v-show="activeStep === STEPS.REVIEW"
                hasBackButton
                :hasRequestValidation="hasRequestValidation"
                :isTranslating="isTranslating"
                :isEditMode="isEditMode"
                :rev="rev"
                :isSaving="isSaving"
                :canImplement="implement"
                :isImplementedValidated="isImplementedValidated"
                :canDoFinancialValidation="canDoFinancialValidation"
                @implementRev="implementRev(revToUpdate)"
                @saveRev="saveRev(revToUpdate)"
                @revChanged="setRevChanged"
                @back="goToStep(4)"
                @createValidationRequest="
                  setValidationRequest($event, revToUpdate)
                "
                @cancelValidationRequest="setCancelRequest($event, revToUpdate)"
                @validate="handleValidateClick"
              />
            </v-card>
          </v-col>
          <v-col md="4">
            <RevSidebar
              submitCommentTestId="improvementIdea_comments_submit"
              modelName="revs"
              :modelType="$t(`common.${modelTypeName}_one`)"
              :hasCommentsActive="true"
              :hasChallengeActive="true"
              :hasAttachmentsActive="true"
              :modelId="revId"
              :canEdit="canComplete"
              :isEditMode="isEditMode"
              :rev="rev"
              :errors="errors"
              :tags="revTags"
              @priorityChanged="updatePriority"
              @onDueDateChange="updateDueDate"
              @onNotesChange="updateNotes"
              @revChanged="setRevChanged"
              @locationChange="locationChange"
            />
          </v-col>
        </v-row>
      </v-container>
      <FindingInfoDialog
        :open="isFindingInfoDialogOpen"
        :title="title"
        @start="handleStart(setEditMode)"
        @close="isFindingInfoDialogOpen = false"
      />
    </div>
  </RevViewWrapper>
</template>

<script>
import { mapActions, mapMutations, mapState, mapGetters } from 'vuex'
import _ from 'lodash'

import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import RevViewWrapper from '@/containers/RevViewWrapper/RevViewWrapper.vue'
import Header from '@/components/Rev/Header.vue'
import RevSidebar from '@/components/Rev/RevSidebar.vue'

import Relate from '@/containers/ImprovementIdeaView/phases/Relate.vue'
import Experiment from '@/containers/ImprovementIdeaView/phases/Experiment.vue'
import Verify from '@/containers/ImprovementIdeaView/phases/Verify.vue'
import Expand from '@/containers/ImprovementIdeaView/phases/Expand.vue'
import Review from '@/containers/ImprovementIdeaView/phases/Review.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import FindingInfoDialog from './FindingInfoDialog.vue'
import ChangedStatusAlert from '@/components/Core/ChangedStatusAlert.vue'
import Stepper from '@/components/Stepper'
import PredictedFinancialImpactAlert from '@/components/Rev/PredictedFinancialImpactAlert.vue'

const STEPS = {
  RELATE: 1,
  EXPERIMENT: 2,
  VERIFY: 3,
  EXPAND: 4,
  REVIEW: 5,
}

export default {
  name: 'ImprovementIdeaView',

  mixins: [mixin, trackerMixin],

  components: {
    RevViewWrapper,
    Header,
    RevSidebar,
    Relate,
    Experiment,
    Verify,
    Expand,
    Review,
    FindingInfoDialog,
    ChangedStatusAlert,
    Stepper,
    PredictedFinancialImpactAlert,
  },

  props: {
    organizationId: {
      type: String,
      required: true,
    },
    revId: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: false,
    },
    initialStep: {
      type: Number,
      required: false,
    },
  },

  data() {
    const isFromFinding = Boolean(this.$route?.params?.isFromFinding)

    return {
      STEPS,
      activeStep: Number(this.initialStep ?? this.$route.query?.tabIndex ?? 1),
      canSave: true,
      showValidate: false,
      isFindingInfoDialogOpen: isFromFinding,
      showStatusModal: false,
    }
  },

  watch: {
    revPhase(newValue) {
      this.updatePhase(newValue)
    },
  },

  computed: {
    ...mapState('improvementIdea', {
      rev: ({ rev }) => rev,
      availableCoaches: state => _.get(state, 'availableCoaches', []),
      availableOwners: state => _.get(state, 'availableOwners', []),
      availableCollaborators: state =>
        _.get(state, 'availableCollaborators', []),
    }),

    ...mapGetters(['organization']),

    modelTypeName() {
      return _.snakeCase(this.rev.type)
    },

    backgroundHeaderImage() {
      return this.rev?.relate?.media?.[0] ?? null
    },

    hasChangedStatusReason() {
      if (this.rev?.status == this.$constants.revStatus.canceled)
        return Boolean(this.rev?.canceledReason)
      if (this.rev?.status == this.$constants.revStatus.onHold)
        return Boolean(this.rev?.onHoldReason)
      return false
    },

    isRelateComplete() {
      const { problem, idea, expectedResult } = this.rev.relate
      const { categories = [] } = this.rev

      return (
        !_.isEmpty(problem) &&
        !_.isEmpty(idea) &&
        !_.isEmpty(expectedResult) &&
        !_.isEmpty(categories)
      )
    },

    isExperimentComplete() {
      const { whatDidYouDo } = this.rev.experiment

      return !_.isEmpty(whatDidYouDo)
    },

    isReviewComplete() {
      return (
        this.isObjectNotEmpty({
          ...this.rev?.review,
          ...this.rev?.reviewPhase,
        }) || Boolean(this.rev?.validation?.financial)
      )
    },

    didExperimentWork() {
      const { didExperimentWork } = this.rev.verify
      return didExperimentWork
    },

    isVerifyComplete() {
      const { didExperimentWork, learned } = this.rev.verify

      if (didExperimentWork) return true
      return !!learned
    },

    isExpandComplete() {
      const { solution, isImplemented, whyNotImplemented } = this.rev.expand

      if (!isImplemented) {
        return !_.isEmpty(solution) && !_.isEmpty(whyNotImplemented)
      }

      return !_.isEmpty(solution)
    },

    revPhase() {
      if (this.isExpandComplete && this.activeStep === 5) {
        return 'review'
      }
      if (this.isVerifyComplete && this.activeStep === 4) {
        return 'expand'
      }
      if (this.isExperimentComplete && this.activeStep === 3) {
        return 'verify'
      }
      if (this.isRelateComplete && this.activeStep === 2) {
        return 'experiment'
      }
      return this.rev.phase
    },

    phases() {
      return [
        {
          name: this.$t('common.rev_state.plan'),
          complete: this.isRelateComplete,
          missingFields: !this.isRelateComplete,
          id: this.$constants.improvementIdea.phases.relate,
        },
        {
          name: this.$t('common.rev_state.do'),
          complete: this.isExperimentComplete,
          missingFields: !this.isExperimentComplete,
          id: this.$constants.improvementIdea.phases.experiment,
        },
        {
          name: this.$t('common.rev_state.check'),
          complete: this.isVerifyComplete,
          missingFields: !this.isVerifyComplete,
          id: this.$constants.improvementIdea.phases.verify,
        },
        {
          name: this.$t('common.rev_state.act'),
          complete: this.isExpandComplete,
          missingFields: !this.hasSolution,
          id: this.$constants.improvementIdea.phases.expand,
        },
        {
          name: this.$t('rev.validation.validate'),
          complete: this.isReviewComplete,
          missingFields: false,
          id: this.$constants.improvementIdea.phases.review,
        },
      ]
    },

    hasSolution() {
      // No solution is required if experiment does not work
      if (!this.rev.verify?.didExperimentWork) return true
      return !_.isEmpty(this.rev?.expand?.solution)
    },

    canCompleteRev() {
      return utils.revs.canComplete({
        type: this.rev.type,
        cycle: {
          relateProblem: this.rev.relate.problem,
          relateIdea: this.rev.relate.idea,
          relateResult: this.rev.relate.expectedResult,
          experimentDone: this.rev.experiment.whatDidYouDo,
          verify: this.rev.verify.didExperimentWork,
          expandSustain: this.rev.expand.solution,
        },
        categories: this.rev.categories,
      })
    },

    revToUpdate() {
      return {
        name: this.rev.name,
        location: {
          site: this.rev.location.site,
          tags: this.rev.location.tags,
        },
        relate: {
          problem: this.rev.relate.problem,
          idea: this.rev.relate.idea,
          expectedResult: this.rev.relate.expectedResult,
          media: this.rev?.relate?.media ?? [],
        },
        experiment: {
          whatDidYouDo: this.rev.experiment.whatDidYouDo,
          media: this.rev?.experiment?.media ?? [],
        },
        verify: {
          didExperimentWork: this.rev.verify.didExperimentWork,
          learned: this.rev.verify.learned,
        },
        expand: {
          media: this.rev?.expand?.media ?? [],
          solution: this.rev.expand.solution,
          isImplemented: this.rev.expand.isImplemented,
          whyNotImplemented: this.rev.expand.whyNotImplemented,
        },
        review: {
          costReduction: this.rev.review.costReduction,
          investment: this.rev.review.investment,
          estimationComment: this.rev.review.estimationComment,
          revenue: this.rev.review.revenue,
          timeInvested: this.rev?.review?.timeInvested,
          timeReductionByMonth: this.rev?.review?.timeReductionByMonth,
        },
        team: {
          owner: this.rev.team.owner.id,
          coach: this.rev.team.coach.id,
          collaborators: this.rev.team.collaborators.map(({ id }) => id),
        },
        categories: this.rev.categories,
        notes: this.rev.notes,
        priority: this.rev.priority,
        dueDate: this.rev.dueDate,
        reviewPhase: this.rev?.reviewPhase ?? {},
        impactStartsAt: this.rev?.impactStartsAt,
        impactEndsAt: this.rev?.impactEndsAt,
        status: this.rev?.status,
      }
    },

    title() {
      return (
        this?.organization?.customNamings?.improvementIdea?.singular ??
        this.$t('common.improvement_idea_one')
      )
    },
  },

  created() {
    this.getRevChallenges({
      organizationId: this.organizationId,
      status: 'active',
      filters: {
        site: this.rev.location.site._id,
      },
    })
  },

  methods: {
    ...mapMutations({
      resetRev: 'improvementIdea/resetRev',
      updatePhase: 'improvementIdea/updatePhase',
      setCoach: 'improvementIdea/setCoach',
      setOwner: 'improvementIdea/setOwner',
      setCollaborators: 'improvementIdea/setCollaborators',
      updateName: 'improvementIdea/updateName',
      updateLocation: 'improvementIdea/updateLocation',
      updatePriority: 'improvementIdea/updatePriority',
      updateDueDate: 'improvementIdea/updateDueDate',
      updateNotes: 'improvementIdea/updateNotes',
    }),

    ...mapActions({
      get: 'improvementIdea/getRev',
      translate: 'improvementIdea/translateRev',
      getRevChallenges: 'improvementIdea/getRevChallenges',
      update: 'improvementIdea/updateRev',
      like: 'improvementIdea/likeRev',
      unlike: 'improvementIdea/unlikeRev',
      approve: 'improvementIdea/approveRev',
      disapprove: 'improvementIdea/disapproveRev',
      delete: 'improvementIdea/deleteRev',
      createValidationRequest: 'improvementIdea/validationRequest',
      cancelRequest: 'improvementIdea/updateRequestStatus',
    }),

    handleStart(setEditMode) {
      setEditMode()
      this.isFindingInfoDialogOpen = false
    },

    complete() {
      return this.updateStatus({
        status: this.$constants.revStatus.completed,
      }).then(this.getRev)
    },

    updateStatus(payload) {
      return this.update({
        organizationId: this.organizationId,
        revId: this.revId,
        data: { ...this.revToUpdate, ...payload },
      }).then(this.getRev)
    },

    trackStatusChanged(status, reason) {
      this.trackRevStatusChanged(
        this.rev,
        this.isEditMode,
        'selector',
        status,
        reason
      )
    },

    handleModalStatusChanged(payload) {
      if (payload.status == this.$constants.revStatus.canceled) {
        this.updateStatus({
          canceledReason: payload.reason,
          status: payload.status,
        })
        this.trackStatusChanged(payload.status, {
          canceledReason: Boolean(payload.reason),
        })
      }
      if (payload.status == this.$constants.revStatus.onHold) {
        this.updateStatus({
          onHoldReason: payload.reason,
          status: payload.status,
        })
        this.trackStatusChanged(payload.status, {
          onHoldReason: Boolean(payload.reason),
        })
      }
      this.showStatusModal = false
    },

    handleStatusChanged(status) {
      if (
        status === this.$constants.revStatus.canceled ||
        status === this.$constants.revStatus.onHold
      ) {
        this.showStatusModal = true
        return this.showStatusModal
      }
      this.update({
        organizationId: this.organizationId,
        revId: this.revId,
        data: { ...this.revToUpdate, status },
      }).then(this.getRev)
      this.trackStatusChanged(status, null)
    },

    updateRev() {
      return this.update({
        organizationId: this.organizationId,
        revId: this.revId,
        data: this.revToUpdate,
      })
    },

    getRev() {
      return this.get({
        organizationId: this.organizationId,
        revId: this.revId,
      })
    },

    goToStep(step, from = 'button') {
      this.trackRevNavigate(this.rev, this.phases[step - 1].id, from)
      if (step === this.activeStep) return

      this.activeStep = step

      this.$router.replace({
        path: this.$route.fullPath,
        query: { ...this.$route.query, tabIndex: step },
      })
    },

    locationChange(attrs) {
      if (attrs.site) {
        this.canSave = true
        this.updateLocation({
          ...attrs,
          organization: { _id: this.organizationId },
        })
      } else {
        this.canSave = false
      }
    },

    async translateRev() {
      try {
        const fieldsToTranslate = [
          'name',
          'cycle.relateProblem',
          'cycle.relateIdea',
          'cycle.relateResult',
          'cycle.expandLearn',
          'cycle.expandSustain',
          'cycle.experimentDone',
          'cycle.reviewComments',
          'cycle.reviewNotImplementedExplanation',
        ]

        await this.translate({
          revId: this.revId,
          organizationId: this.organizationId,
          targetLanguage: this.getLocaleWithoutDialect(
            this.$store.getters.language
          ),
          fieldsToTranslate,
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async deleteRev() {
      this.trackDelete(this.rev, 'improvementIdea', 'confirm')
      return this.delete({
        organizationId: this.organizationId,
        revId: this.rev.id,
      })
        .then(() => {
          this.trackDelete(this.rev, 'improvementIdea', 'complete', 'success')
        })
        .catch(err => {
          this.trackDelete(this.rev, 'improvementIdea', 'complete', 'fail', err)
        })
    },

    trackUpdateRev(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    trackPrintRev() {
      this.trackPrint(this.rev)
    },

    trackCancelDelete() {
      this.trackDelete(this.rev, 'improvementIdea', 'cancel')
    },

    trackStartDelete() {
      this.trackDelete(this.rev, 'improvementIdea', 'start')
    },

    trackNavigateRev(phase) {
      this.trackRevNavigate(this.rev, phase, 'tab')
    },

    handleValidateClick() {
      this.trackClickValidateIcon({ action: 'goToValidationView' })

      return this.navigateTo({
        name: 'validateRev',
        params: {
          revId: this.revId,
          revType: 'improvement-idea',
        },
      })
    },
  },
  beforeRouteLeave(to, from, next) {
    eventHub.$emit(HUB_EVENTS.REV_VIEW.BEFORE_ROUTE_LEAVE, { to, from, next })
  },
}
</script>
